window.LadaRitualEngine = (() => {
  const STORAGE_PREFIX = 'lada_ritual_';
  const RANKING_KEY = `${STORAGE_PREFIX}ranking`;
  const GLOBAL_STATE_KEY = `${STORAGE_PREFIX}global_state`;
  const SAVED_RITUALS_KEY = `${STORAGE_PREFIX}saved_items`;

  const qs = (scope, selector) => (scope ? scope.querySelector(selector) : null);
  const qsa = (scope, selector) => (scope ? Array.from(scope.querySelectorAll(selector)) : []);

  function safeJSONParse(value, fallback) {
    try {
      if (!value) return fallback;
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch (error) {
      return fallback;
    }
  }

  function todayKey() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  function getWeekKey(date = new Date()) {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
  }

  function formatTime(seconds) {
    const safe = Math.max(0, Number(seconds) || 0);
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    return {
      min: String(m).padStart(2, '0'),
      sec: String(s).padStart(2, '0')
    };
  }

  function getDefaultState() {
    return {
      completedDates: [],
      lastCompletedAt: null,
      streak: 0,
      weekly: {},
      dailyCounts: {},
      saved: false,
      totalCompletions: 0
    };
  }

  function getDefaultGlobalState() {
    return {
      completedDates: [],
      weekly: {},
      dailyCounts: {},
      totalCompletions: 0
    };
  }

  function getState(ritualId) {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${ritualId}`);
    const parsed = safeJSONParse(raw, null);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return getDefaultState();
    }

    return {
      completedDates: Array.isArray(parsed.completedDates) ? parsed.completedDates : [],
      lastCompletedAt: parsed.lastCompletedAt || null,
      streak: Number(parsed.streak || 0),
      weekly: parsed.weekly && typeof parsed.weekly === 'object' ? parsed.weekly : {},
      dailyCounts: parsed.dailyCounts && typeof parsed.dailyCounts === 'object' ? parsed.dailyCounts : {},
      saved: !!parsed.saved,
      totalCompletions: Number(parsed.totalCompletions || 0)
    };
  }

  function setState(ritualId, data) {
    localStorage.setItem(`${STORAGE_PREFIX}${ritualId}`, JSON.stringify(data));
  }

  function getGlobalState() {
    const raw = localStorage.getItem(GLOBAL_STATE_KEY);
    const parsed = safeJSONParse(raw, null);

   if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
     return getDefaultGlobalState();
    }

    return {
      completedDates: Array.isArray(parsed.completedDates) ? parsed.completedDates : [],
      weekly: parsed.weekly && typeof parsed.weekly === 'object' ? parsed.weekly : {},
      dailyCounts: parsed.dailyCounts && typeof parsed.dailyCounts === 'object' ? parsed.dailyCounts : {},
      totalCompletions: Number(parsed.totalCompletions || 0)
    };
  }

  function setGlobalState(data) {
    localStorage.setItem(GLOBAL_STATE_KEY, JSON.stringify(data));
  }

  function getSavedRituals() {
    const raw = localStorage.getItem(SAVED_RITUALS_KEY);
    const parsed = safeJSONParse(raw, []);
    return Array.isArray(parsed) ? parsed : [];
  }

  function setSavedRituals(items) {
    localStorage.setItem(SAVED_RITUALS_KEY, JSON.stringify(items));
  }

  function saveRitualItem(item) {
    const items = getSavedRituals();
    const existsIndex = items.findIndex((x) => x.id === item.id);

    if (existsIndex >= 0) {
      items[existsIndex] = {
        ...items[existsIndex],
        ...item,
        savedAt: Date.now()
      };
    } else {
      items.unshift({
        ...item,
        savedAt: Date.now()
      });
    }

    setSavedRituals(items);
    return items;
  }

  function removeSavedRitualItem(ritualId) {
    const items = getSavedRituals().filter((x) => x.id !== ritualId);
    setSavedRituals(items);
    return items;
  }

  function computeStreak(completedDates) {
    if (!Array.isArray(completedDates) || !completedDates.length) return 0;

    const unique = [...new Set(completedDates)].sort();
    let streak = 0;
    const cursor = new Date();
    cursor.setHours(0, 0, 0, 0);

    while (true) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
      if (unique.includes(key)) {
        streak += 1;
        cursor.setDate(cursor.getDate() - 1);
      } else {
        break;
      }
    }

    if (streak === 0) {
      const y = new Date();
      y.setHours(0, 0, 0, 0);
      y.setDate(y.getDate() - 1);

      while (true) {
        const key = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, '0')}-${String(y.getDate()).padStart(2, '0')}`;
        if (unique.includes(key)) {
          streak += 1;
          y.setDate(y.getDate() - 1);
        } else {
          break;
        }
      }
    }

    return streak;
  }

  function getRanking() {
    return safeJSONParse(localStorage.getItem(RANKING_KEY), {});
  }

  function setRanking(data) {
    localStorage.setItem(RANKING_KEY, JSON.stringify(data));
  }

  function updateRanking(ritualId, ritualTitle) {
    const ranking = getRanking();

    ranking[ritualId] = ranking[ritualId] || {
      id: ritualId,
      title: ritualTitle || ritualId,
      total: 0
    };

    ranking[ritualId].title = ritualTitle || ranking[ritualId].title || ritualId;
    ranking[ritualId].total += 1;

    setRanking(ranking);
    return ranking;
  }

  function getSortedRanking() {
    const ranking = getRanking();
    return Object.values(ranking).sort((a, b) => b.total - a.total);
  }

  function markCompleted(ritualId, weeklyGoal, ritualTitle) {
    const state = getState(ritualId);
    const today = todayKey();
    const weekKey = getWeekKey();

    if (!state.completedDates.includes(today)) {
      state.completedDates.push(today);
    }

    state.weekly = state.weekly || {};
    state.weekly[weekKey] = Array.isArray(state.weekly[weekKey]) ? state.weekly[weekKey] : [];

    if (!state.weekly[weekKey].includes(today)) {
      state.weekly[weekKey].push(today);
    }

    state.dailyCounts = state.dailyCounts || {};
    state.dailyCounts[today] = Number(state.dailyCounts[today] || 0) + 1;

    state.lastCompletedAt = Date.now();
    state.streak = computeStreak(state.completedDates);
    state.totalCompletions = Number(state.totalCompletions || 0) + 1;

    setState(ritualId, state);
    updateRanking(ritualId, ritualTitle);

    return {
      totalDone: state.completedDates.length,
      todayDone: Number(state.dailyCounts[today] || 0),
      streak: state.streak,
      weeklyDone: Math.min((state.weekly[weekKey] || []).length, weeklyGoal),
      saved: !!state.saved,
      totalCompletions: state.totalCompletions
    };
  }

  function markCompletedGlobal(weeklyGoal) {
    const state = getGlobalState();
    const today = todayKey();
    const weekKey = getWeekKey();

    if (!state.completedDates.includes(today)) {
      state.completedDates.push(today);
    }

    state.weekly = state.weekly || {};
    state.weekly[weekKey] = Array.isArray(state.weekly[weekKey]) ? state.weekly[weekKey] : [];

    if (!state.weekly[weekKey].includes(today)) {
      state.weekly[weekKey].push(today);
    }

    state.dailyCounts = state.dailyCounts || {};
    state.dailyCounts[today] = (state.dailyCounts[today] || 0) + 1;

    state.totalCompletions = Number(state.totalCompletions || 0) + 1;
    setGlobalState(state);

    return {
      totalDone: state.totalCompletions,
      todayDone: state.dailyCounts?.[today] || 0,
      streak: computeStreak(state.completedDates),
      weeklyDone: Math.min((state.weekly[weekKey] || []).length, weeklyGoal)
    };
  }

  function getProgressSnapshot(ritualId, weeklyGoal) {
    const state = getState(ritualId);
    const today = todayKey();
    const weekKey = getWeekKey();

    return {
      totalDone: state.completedDates.length,
      streak: state.streak || computeStreak(state.completedDates),
      todayDone: Number((state.dailyCounts || {})[today] || 0),
      weeklyDone: Math.min(((state.weekly && state.weekly[weekKey]) || []).length, weeklyGoal),
      saved: !!state.saved,
      totalCompletions: Number(state.totalCompletions || 0)
    };
  }

  function getGlobalProgressSnapshot(weeklyGoal) {
    const state = getGlobalState();
    const today = todayKey();
    const weekKey = getWeekKey();

    return {
      totalDone: Number(state.totalCompletions || 0),
      streak: computeStreak(state.completedDates),
      todayDone: Number((state.dailyCounts || {})[today] || 0),
      weeklyDone: Math.min(((state.weekly && state.weekly[weekKey]) || []).length, weeklyGoal)
    };
  }

  function seededNumber(seed) {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i += 1) {
      h ^= seed.charCodeAt(i);
      h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
    }
    return Math.abs(h >>> 0);
  }

  function getDailyPeopleCount(ritualId, base, variance) {
    const storageKey = `${STORAGE_PREFIX}${ritualId}_people_count`;
    const today = todayKey();
    const safeBase = Number(base) || 200;
    const safeVariance = Math.max(1, Number(variance) || 18);

    const stored = safeJSONParse(localStorage.getItem(storageKey), null);

    if (stored && stored.date === today && Number.isFinite(Number(stored.count))) {
      return Number(stored.count);
    }

    const seeded = seededNumber(`${ritualId}-${today}`);
    const startOffset = seeded % (safeVariance + 1);
    const startCount = Math.max(1, safeBase + startOffset);

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        date: today,
        count: startCount
      })
    );

    return startCount;
  }

  function bumpDailyPeopleCount(ritualId, base, variance, step = 1) {
    const storageKey = `${STORAGE_PREFIX}${ritualId}_people_count`;
    const today = todayKey();
    const current = getDailyPeopleCount(ritualId, base, variance);
    const next = Math.max(current, current + Math.max(1, Number(step) || 1));

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        date: today,
        count: next
      })
    );

    return next;
  }

  function getLivePeopleCount(ritualId, base, variance) {
    const dailyBase = getDailyPeopleCount(ritualId, base, variance);
    const now = new Date();
    const minutesToday = now.getHours() * 60 + now.getMinutes();
    const liveSeed = seededNumber(`${ritualId}-${todayKey()}-live`);
    const maxExtra = Math.max(4, Math.floor((Number(variance) || 18) * 0.9));
    const progressiveExtra = Math.floor((minutesToday / 1440) * maxExtra);
    const microExtra = liveSeed % 7;
    return dailyBase + progressiveExtra + microExtra;
  }


  function animateNumber(el, target, duration = 800) {
    if (!el) return;

    const currentText = String(el.textContent || '').replace(/[^\d]/g, '');
    const start = Number(currentText || 0);
    const end = Number(target || 0);

    if (start === end) {
      el.textContent = end;
      return;
    }

    el.classList.remove('lada-stat-bump');
    void el.offsetWidth;
    el.classList.add('lada-stat-bump');

    const startTime = performance.now();

    function frame(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(start + (end - start) * eased);
      el.textContent = value;

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        window.setTimeout(() => {
          el.classList.remove('lada-stat-bump');
        }, 220);
      }
    }

    requestAnimationFrame(frame);
  }

  function updateStatsUI(root, snapshot, weeklyGoal) {
    const totalEl = qs(root, '[data-lada-total-done]');
    const streakEl = qs(root, '[data-lada-streak]');
    const todayEl = qs(root, '[data-lada-today]');
    const weekEl = qs(root, '[data-lada-weekly]');
    const savedBadge = qs(root, '[data-lada-saved-badge]');

    animateNumber(totalEl, snapshot.totalDone);
    animateNumber(streakEl, snapshot.streak);
    animateNumber(todayEl, snapshot.todayDone);

    if (weekEl) {
      weekEl.textContent = `${snapshot.weeklyDone}/${weeklyGoal}`;
    }

    if (savedBadge) {
      savedBadge.hidden = !snapshot.saved;
      savedBadge.classList.toggle('is-visible', snapshot.saved);
    }
  }

  function updateProgressBar(root, currentStep, totalSteps) {
    const bar = qs(root, '[data-lada-progress-bar]');
    const stickyBar = qs(root, '[data-lada-progress-bar-sticky]');
    const label = qs(root, '[data-lada-progress-label]');
    const stickyLabel = qs(root, '[data-lada-progress-label-sticky]');

    const safeTotal = Math.max(1, Number(totalSteps) || 1);
    const safeCurrent = Math.min(Math.max(Number(currentStep) || 1, 1), safeTotal);
    const percent = Math.round((safeCurrent / safeTotal) * 100);
    const text = `Paso ${safeCurrent} de ${safeTotal}`;

    if (bar) bar.style.width = `${percent}%`;
    if (stickyBar) stickyBar.style.width = `${percent}%`;
    if (label) label.textContent = text;
    if (stickyLabel) stickyLabel.textContent = text;
  }

  function updateTimerProgress(root, elapsed, total) {
    const bar = qs(root, '[data-lada-progress-bar-timer]');
    const label = qs(root, '[data-lada-progress-label-timer]');

    const safeTotal = Math.max(1, Number(total) || 1);
    const safeElapsed = Math.min(Math.max(Number(elapsed) || 0, 0), safeTotal);
    const percent = Math.round((safeElapsed / safeTotal) * 100);

    if (bar) bar.style.width = `${percent}%`;
    if (label) label.textContent = `${percent}% completado`;
  }

  function setActiveStep(root, index) {
    const cards = qsa(root, '[data-lada-step-card]');
    const syncItems = qsa(root, '[data-lada-step-title-sync]');

    if (!cards.length) return;

    const safeIndex = Math.min(Math.max(index, 0), cards.length - 1);

    cards.forEach((card, i) => {
      card.classList.toggle('is-active', i === safeIndex);
    });

    syncItems.forEach((item, i) => {
      item.classList.toggle('is-active', i === safeIndex);
    });

    updateProgressBar(root, safeIndex + 1, cards.length);
  }

  function setupStepObserver(root) {
    const cards = qsa(root, '[data-lada-step-card]');
    if (!cards.length) return;

    setActiveStep(root, 0);

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let visibleEntry = null;

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!visibleEntry || entry.intersectionRatio > visibleEntry.intersectionRatio) {
            visibleEntry = entry;
          }
        });

        if (!visibleEntry) return;

        const idx = Number(visibleEntry.target.getAttribute('data-step-index'));
        if (!Number.isNaN(idx)) {
          setActiveStep(root, idx);
        }
      },
      {
        root: null,
        threshold: [0.2, 0.45, 0.7]
      }
    );

    cards.forEach((card) => observer.observe(card));
  }

  function ensureOverlay() {
    let overlay = document.querySelector('.lada-immersive-overlay');

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'lada-immersive-overlay';
      document.body.appendChild(overlay);
    }

    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
    });

    return overlay;
  }

  function removeOverlay() {
    const overlay = document.querySelector('.lada-immersive-overlay');
    if (!overlay) return;

    overlay.classList.remove('is-visible');

    window.setTimeout(() => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    }, 320);
  }

  function enterImmersive(root) {
    root.classList.add('is-immersive');
    document.documentElement.classList.add('lada-immersive-on');
    document.body.classList.add('lada-immersive-on');
    ensureOverlay();
  }

  function exitImmersive(root) {
    root.classList.remove('is-immersive');
    document.documentElement.classList.remove('lada-immersive-on');
    document.body.classList.remove('lada-immersive-on');
    removeOverlay();
  }

  function setupEmotionChips(root) {
    const chips = qsa(root, '[data-lada-emotion-chip]');
    if (!chips.length) return;

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        chips.forEach((c) => c.classList.remove('is-active'));
        chip.classList.add('is-active');
      });
    });
  }

  function syncStepTitles(root) {
    const sourceTitles = qsa(root, '[data-lada-step-title-source]')
      .map((el) => el.textContent.trim())
      .filter(Boolean);

    const targets = qsa(root, '[data-lada-step-title-sync]');
    targets.forEach((target, i) => {
      if (sourceTitles[i]) {
        target.textContent = `${i + 1}. ${sourceTitles[i]}`;
      }
    });
  }

  function setupSmoothScroll(root) {
    qsa(root, '[data-lada-scroll]').forEach((btn) => {
      if (btn.dataset.ladaBoundScroll === 'true') return;

      btn.dataset.ladaBoundScroll = 'true';

      btn.addEventListener('click', () => {
        const selector = btn.getAttribute('data-lada-scroll');
        if (!selector) return;

        const target = root.querySelector(selector) || document.querySelector(selector);
        if (!target) return;

        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    });
  }

  function showFeedback(root, message) {
    const feedback =
      qs(root, '[data-lada-feedback]') ||
      qs(root.parentNode, '[data-lada-feedback]') ||
      qs(document, '[data-lada-feedback]');

    if (!feedback) return;

    feedback.textContent = message;
    feedback.classList.add('is-visible');

    clearTimeout(feedback._ladaTimeout);
    feedback._ladaTimeout = setTimeout(() => {
      feedback.classList.remove('is-visible');
    }, 2200);
  }

  function flashFeedback(root, message) {
    showFeedback(root, message);
  }

  function syncBreathVisual(root, phaseName) {
    const media = qs(root, '.lada-breath__media');
    const symbol = qs(root, '.lada-breath__symbol');

    if (!media) return;

    media.classList.remove('is-inhale', 'is-hold', 'is-exhale');
    if (symbol) {
      symbol.classList.remove('is-inhale', 'is-hold', 'is-exhale');
    }

    if (phaseName === 'Inhala') {
      media.classList.add('is-inhale');
      if (symbol) symbol.classList.add('is-inhale');
    }

    if (phaseName === 'Sostén') {
      media.classList.add('is-hold');
      if (symbol) symbol.classList.add('is-hold');
    }

    if (phaseName === 'Exhala') {
      media.classList.add('is-exhale');
      if (symbol) symbol.classList.add('is-exhale');
    }
  }

  function celebrateCompletion(root) {
    root.classList.add('lada-glow', 'is-complete');

    window.setTimeout(() => {
      root.classList.remove('lada-glow', 'is-complete');
    }, 2400);
  }

  function vibrate(pattern) {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  }

  function applyNightMode(root) {
    const prefersDark =
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;

    const hour = new Date().getHours();
    const byHour = hour >= 20 || hour < 7;
    const isNight = prefersDark || byHour;

    root.classList.toggle('lada-night-mode', isNight);
  }

  function renderRanking(root) {
    const list = qs(root, '[data-lada-ranking-list]');
    if (!list) return;

    const items = getSortedRanking().slice(0, 5);

    if (!items.length) {
      list.innerHTML = '<li class="lada-ranking__item"><span>Aún no hay datos</span><strong>0</strong></li>';
      return;
    }

    list.innerHTML = items
      .map((item, index) => {
        return `
          <li class="lada-ranking__item">
            <span>${index + 1}. ${item.title}</span>
            <strong>${item.total}</strong>
          </li>
        `;
      })
      .join('');
  }

  function initRitual(root) {
    if (!root || root.dataset.ladaEngineReady === 'true') return;
    root.dataset.ladaEngineReady = 'true';

    const ritualId = root.dataset.ritualId || 'ritual';
    const ritualTitle =
      root.dataset.ritualTitle ||
      qs(root, '.lada-title')?.textContent?.trim() ||
      ritualId;
    const ritualUrl = root.dataset.ritualUrl || window.location.pathname;
    const ritualTheme = root.dataset.ritualTheme || 'calma';

    const weeklyGoal = Number(root.dataset.weeklyGoal || 5);
    const peopleBase = Number(root.dataset.peopleBase || 200);
    const peopleVariance = Number(root.dataset.peopleVariance || 18);

    const startBtn = qs(root, '[data-lada-start]');
    const pauseBtn = qs(root, '[data-lada-pause]');
    const resetBtn = qs(root, '[data-lada-reset]');

    const minEl = qs(root, '[data-lada-min]');
    const secEl = qs(root, '[data-lada-sec]');
    const phaseEl = qs(root, '[data-lada-phase]');
    const coachEl = qs(root, '[data-lada-coach]');

    const audioEl = qs(root, '[data-lada-audio]');
    const audioToggleMain = qs(root, '[data-lada-audio-toggle]');
    const audioToggleSecondary = qs(root, '[data-lada-audio-toggle-secondary]');
    const audioIcon = qs(root, '[data-lada-audio-icon]');
    const audioLabel = qs(root, '[data-lada-audio-label]');
    const audioLabelSecondary = qs(root, '[data-lada-audio-label-secondary]');

    const finishSentinel = qs(root, '[data-lada-finish-sentinel]');
    const peopleEl = qs(root, '[data-lada-people-count]');
    const saveBtn = qs(root, '[data-lada-save]');
    const saveText = qs(root, '[data-lada-save-text]');
    const silenceCard = qs(root, '[data-lada-silence]');
    const completeCard = qs(root, '[data-lada-complete]');
    const completionAnchor = qs(root, '[data-lada-completion-anchor]');
    const bellUrl = root.dataset.bellUrl || '';
    const bellAudio = bellUrl ? new Audio(bellUrl) : null;

    if (bellAudio) {
      bellAudio.preload = 'auto';
     bellAudio.volume = 0.35;
    }

    const breathCoach = root.dataset.breathCoach || 'Inhala 4 · Sostén 2 · Exhala 6';
    const breathRunning = root.dataset.breathRunning || 'Ritmo suave. Exhalación larga.';
    const breathDone = root.dataset.breathDone || 'Calma presente. Centro abierto.';
    const breathStartLabel = root.dataset.breathStartLabel || 'Empezar';
    const breathLiveLabel = root.dataset.breathLiveLabel || 'En curso';
    const breathPauseLabel = root.dataset.breathPauseLabel || 'Pausa';
    const breathResumeLabel = root.dataset.breathResumeLabel || 'Reanudar';
    const breathResetLabel = root.dataset.breathResetLabel || 'Reiniciar';
    const audioPlayLabel = root.dataset.audioPlayLabel || 'Escuchar';
    const audioPauseLabel = root.dataset.audioPauseLabel || 'Pausar audio';

    const inhaleSeconds = Math.max(1, Number(root.dataset.inhaleSeconds || 4));
    const holdSeconds = Math.max(1, Number(root.dataset.holdSeconds || 2));
    const exhaleSeconds = Math.max(1, Number(root.dataset.exhaleSeconds || 6));

    const cycles = [
      { name: 'Inhala', seconds: inhaleSeconds },
      { name: 'Sostén', seconds: holdSeconds },
      { name: 'Exhala', seconds: exhaleSeconds }
    ];

    const modeButtons = qsa(root, '[data-lada-mode]');
    const activeModeBtn =
      modeButtons.find((btn) => btn.classList.contains('is-active')) || modeButtons[0];

    let total = Number(activeModeBtn?.dataset.ladaMode || root.dataset.mode1Seconds || 150);
    let remaining = total;
    let interval = null;
    let completedByTimer = false;
    let completedByScroll = false;
    let cycleIndex = 0;
    let cycleRemaining = cycles[0].seconds;

    const localSnapshot = getProgressSnapshot(ritualId, weeklyGoal);
    const globalSnapshot = getGlobalProgressSnapshot(weeklyGoal);

    updateStatsUI(
      root,
      {
        totalDone: globalSnapshot.totalDone,
        streak: globalSnapshot.streak,
        todayDone: globalSnapshot.todayDone,
        weeklyDone: globalSnapshot.weeklyDone,
        saved: localSnapshot.saved,
        totalCompletions: localSnapshot.totalCompletions
      },
      weeklyGoal
    );
    renderRanking(root);
    applyNightMode(root);

    if (silenceCard) {
      silenceCard.hidden = true;
      silenceCard.classList.remove('is-visible');
    }

    if (completeCard) {
      completeCard.hidden = true;
      completeCard.classList.remove('is-visible');
    }

    if (peopleEl) {
        const peopleStorageKey = `lada_people_${ritualId}_${todayKey()}`;

        let livePeopleCount = Number(localStorage.getItem(peopleStorageKey));

        if (!livePeopleCount) {
          livePeopleCount = getLivePeopleCount(ritualId, peopleBase, peopleVariance);
          localStorage.setItem(peopleStorageKey, livePeopleCount);
        }


      animateNumber(peopleEl, livePeopleCount, 1200);

      const liveTick = window.setInterval(() => {
       const incrementSeed = seededNumber(`${ritualId}-${todayKey()}-${new Date().getHours()}-${new Date().getMinutes()}`);
       const shouldIncrease = incrementSeed % 4 === 0;

       if (shouldIncrease) {
         livePeopleCount += 1;
         localStorage.setItem(peopleStorageKey, livePeopleCount);
         animateNumber(peopleEl, livePeopleCount, 600);
       }
     }, 25000);

     root._ladaPeopleInterval = liveTick;
    }

    if (saveBtn && saveText) {
      const state = getState(ritualId);
      saveBtn.classList.toggle('is-saved', !!state.saved);
      saveText.textContent = state.saved
        ? (root.dataset.savedLabel || 'Ritual guardado')
        : (root.dataset.saveLabel || 'Guardar ritual');

      saveBtn.addEventListener('click', () => {
        const current = getState(ritualId);
        current.saved = !current.saved;
        setState(ritualId, current);

        if (current.saved) {
          saveRitualItem({
            id: ritualId,
            title: ritualTitle,
            url: ritualUrl,
            theme: ritualTheme
          });
        } else {
          removeSavedRitualItem(ritualId);
        }

        saveBtn.classList.toggle('is-saved', current.saved);
        saveText.textContent = current.saved
          ? (root.dataset.savedLabel || 'Ritual guardado')
          : (root.dataset.saveLabel || 'Guardar ritual');

        const globalSnapshot = getGlobalProgressSnapshot(weeklyGoal);
        updateStatsUI(
          root,
          {
            totalDone: globalSnapshot.totalDone,
            streak: globalSnapshot.streak,
            todayDone: globalSnapshot.todayDone,
            weeklyDone: globalSnapshot.weeklyDone,
            saved: current.saved,
            totalCompletions: Number(current.totalCompletions || 0)
          },
          weeklyGoal
        );

        flashFeedback(root, current.saved ? 'Ritual guardado' : 'Guardado quitado');
        vibrate(20);
      });
    }

    function renderTime() {
      const time = formatTime(remaining);

      if (minEl) minEl.textContent = time.min;
      if (secEl) secEl.textContent = time.sec;

      updateTimerProgress(root, total - remaining, total);
    }

    function renderPhase() {
      const phase = cycles[cycleIndex].name;

      if (phaseEl) phaseEl.textContent = phase;
      syncBreathVisual(root, phase);
    }

    function getButtonTextEl(btn) {
      return btn ? btn.querySelector('span:last-child') : null;
    }

    function setButtonLabels(state) {
      const startText = getButtonTextEl(startBtn);
      const pauseText = getButtonTextEl(pauseBtn);
      const resetText = getButtonTextEl(resetBtn);

      if (state === 'idle') {
        if (startBtn) startBtn.disabled = false;
        if (pauseBtn) pauseBtn.disabled = true;
        if (pauseBtn) pauseBtn.setAttribute('aria-disabled', 'true');
        if (startText) startText.textContent = breathStartLabel;
        if (pauseText) pauseText.textContent = breathPauseLabel;
        if (resetText) resetText.textContent = breathResetLabel;
      }

      if (state === 'running') {
        if (startBtn) startBtn.disabled = true;
        if (pauseBtn) pauseBtn.disabled = false;
        if (pauseBtn) pauseBtn.setAttribute('aria-disabled', 'false');
        if (startText) startText.textContent = breathLiveLabel;
        if (pauseText) pauseText.textContent = breathPauseLabel;
        if (resetText) resetText.textContent = breathResetLabel;
      }

      if (state === 'paused') {
        if (startBtn) startBtn.disabled = false;
        if (pauseBtn) pauseBtn.disabled = true;
        if (pauseBtn) pauseBtn.setAttribute('aria-disabled', 'true');
        if (startText) startText.textContent = breathResumeLabel;
        if (pauseText) pauseText.textContent = breathPauseLabel;
        if (resetText) resetText.textContent = breathResetLabel;
      }
    }

    function updateAudioUI(playing) {
      if (audioIcon) audioIcon.textContent = playing ? '⏸' : '▶';
      if (audioLabel) audioLabel.textContent = playing ? audioPauseLabel : audioPlayLabel;
      if (audioLabelSecondary) {
        audioLabelSecondary.textContent = playing ? audioPauseLabel : audioPlayLabel;
      }
    }

    async function playAudio() {
      if (!audioEl) return;
      try {
        await audioEl.play();
      } catch (error) {}
      updateAudioUI(!audioEl.paused);
    }

    function pauseAudio() {
      if (!audioEl) return;
      try {
        audioEl.pause();
      } catch (error) {}
      updateAudioUI(false);
    }

    function stopAudio() {
      if (!audioEl) return;
      try {
        audioEl.pause();
        audioEl.currentTime = 0;
      } catch (error) {}
      updateAudioUI(false);
    }

    function fadeOutAudio(duration = 800, callback) {
      if (!audioEl) {
        if (typeof callback === 'function') callback();
        return;
      }

      const startVolume = typeof audioEl.volume === 'number' ? audioEl.volume : 1;
      const steps = 16;
      const stepTime = Math.max(20, Math.floor(duration / steps));
      let currentStep = 0;

      try {
        const fadeInterval = window.setInterval(() => {
          currentStep += 1;
          const progress = currentStep / steps;
          const nextVolume = Math.max(0, startVolume * (1 - progress));

          audioEl.volume = nextVolume;

          if (currentStep >= steps) {
            window.clearInterval(fadeInterval);

            try {
              audioEl.pause();
              audioEl.currentTime = 0;
              audioEl.volume = startVolume;
            } catch (e) {}

            updateAudioUI(false);

            if (typeof callback === 'function') {
              callback();
            }
          }
        }, stepTime);
      } catch (e) {
        try {
          audioEl.pause();
          audioEl.currentTime = 0;
          audioEl.volume = startVolume;
        } catch (err) {}

        updateAudioUI(false);

        if (typeof callback === 'function') {
          callback();
        }
      }
    }

    function playBell() {
      if (!bellAudio) return;

      try {
        bellAudio.pause();
        bellAudio.currentTime = 0;
        bellAudio.play().catch(() => {});
      } catch (e) {}
    }

   function showSilenceCard() {
     if (!silenceCard) return;
     silenceCard.hidden = false;
     requestAnimationFrame(() => {
       silenceCard.classList.add('is-visible');
     });
    }

    function hideSilenceCard() {
      if (!silenceCard) return;
      silenceCard.classList.remove('is-visible');
      silenceCard.hidden = true;
    }

    function showCompleteCard() {
      if (!completeCard) return;
      completeCard.hidden = false;
      requestAnimationFrame(() => {
        completeCard.classList.add('is-visible');
      });
    }

    function hideCompleteCard() {
      if (!completeCard) return;
      completeCard.classList.remove('is-visible');
      completeCard.hidden = true;
    }

    function scrollToCompletionArea(targetEl = null, extraOffset = 0) {
      const target = targetEl || completionAnchor || silenceCard || completeCard;
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const absoluteTop = window.pageYOffset + rect.top;
      const finalTop = Math.max(0, absoluteTop - 140 + extraOffset);

      window.scrollTo({
        top: finalTop,
        behavior: 'smooth'
      });
    }

    function completeRitualIfNeeded() {
      if (completedByTimer || completedByScroll) return;

      completedByTimer = true;

      const localSnap = markCompleted(ritualId, weeklyGoal, ritualTitle);
      const globalSnap = markCompletedGlobal(weeklyGoal);

      updateStatsUI(
        root,
        {
          totalDone: globalSnap.totalDone,
          streak: globalSnap.streak,
          todayDone: globalSnap.todayDone,
          weeklyDone: globalSnap.weeklyDone,
          saved: localSnap.saved,
          totalCompletions: localSnap.totalCompletions
      },
      weeklyGoal
    );

    renderRanking(root);
    celebrateCompletion(root);
    flashFeedback(root, 'Ritual completado');
    vibrate([40, 60, 40]);
  }

    function finishRitual() {
      clearInterval(interval);
      interval = null;
      remaining = 0;
      renderTime();

      if (coachEl) {
        coachEl.textContent = breathDone;
      }

      setButtonLabels('idle');
      exitImmersive(root);
      completeRitualIfNeeded();

      hideSilenceCard();
      hideCompleteCard();

      fadeOutAudio(800, () => {
        window.setTimeout(() => {
          playBell();
          showFeedback(root, 'Ritual completado');

          window.setTimeout(() => {
            showSilenceCard();

            window.setTimeout(() => {
              scrollToCompletionArea(silenceCard, -10);
            }, 120);

            window.setTimeout(() => {
              hideSilenceCard();
              showCompleteCard();

              window.setTimeout(() => {
                scrollToCompletionArea(completeCard, -10);
              }, 120);
            }, 3400);
          }, 450);
        }, 500);
      });
    }

    function tick() {
      if (remaining <= 0) {
        finishRitual();
        return;
      }

      remaining -= 1;
      cycleRemaining -= 1;

      if (cycleRemaining <= 0) {
        cycleIndex = (cycleIndex + 1) % cycles.length;
        cycleRemaining = cycles[cycleIndex].seconds;
        renderPhase();
      }

      renderTime();

      if (remaining <= 0) {
        finishRitual();
      }
    }

    async function startRitual() {
      if (interval) return;

      hideSilenceCard();
      hideCompleteCard();

      if (coachEl) coachEl.textContent = breathRunning;

      setButtonLabels('running');
      enterImmersive(root);
      vibrate(40);

      await playAudio();
      renderPhase();
      renderTime();

      interval = window.setInterval(tick, 1000);
      flashFeedback(root, 'Ritual iniciado');
    }

    function pauseRitual() {
      clearInterval(interval);
      interval = null;

      if (coachEl) coachEl.textContent = 'Pausa suave.';

      setButtonLabels('paused');
      exitImmersive(root);
      pauseAudio();
      vibrate(20);
      flashFeedback(root, 'Ritual en pausa');
    }

    function resetRitual() {
      clearInterval(interval);
      interval = null;

      const active = modeButtons.find((btn) => btn.classList.contains('is-active'));
      total = Number(active?.dataset.ladaMode || root.dataset.mode1Seconds || 150);
      remaining = total;
      cycleIndex = 0;
      cycleRemaining = cycles[0].seconds;
      completedByTimer = false;

      renderTime();
      renderPhase();

      if (coachEl) coachEl.textContent = breathCoach;

      setButtonLabels('idle');
      exitImmersive(root);
      stopAudio();
      hideSilenceCard();
      hideCompleteCard();
      flashFeedback(root, 'Ritual reiniciado');
    }

    modeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        modeButtons.forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        resetRitual();
      });
    });

    if (audioToggleMain) {
      audioToggleMain.addEventListener('click', async () => {
        if (!audioEl) return;

        if (audioEl.paused) {
          await playAudio();
          flashFeedback(root, 'Audio activado');
        } else {
          pauseAudio();
          flashFeedback(root, 'Audio pausado');
        }
      });
    }

    if (audioToggleSecondary) {
      audioToggleSecondary.addEventListener('click', async () => {
        if (!audioEl) return;

        if (audioEl.paused) {
          await playAudio();
          flashFeedback(root, 'Audio activado');
        } else {
          pauseAudio();
          flashFeedback(root, 'Audio pausado');
        }
      });
    }

    if (audioEl) {
      audioEl.addEventListener('play', () => updateAudioUI(true));
      audioEl.addEventListener('pause', () => updateAudioUI(false));
      audioEl.addEventListener('ended', () => updateAudioUI(false));
    }

    if (startBtn) startBtn.addEventListener('click', startRitual);
    if (pauseBtn) pauseBtn.addEventListener('click', pauseRitual);
    if (resetBtn) resetBtn.addEventListener('click', resetRitual);

    const copyBtn = qs(root, '[data-lada-copy]');
    const clearBtn = qs(root, '[data-lada-clear]');
    const textarea = qs(root, '.lada-textarea');

    if (copyBtn && textarea) {
      copyBtn.addEventListener('click', async () => {
        const value = textarea.value.trim();
        if (!value) return;

        try {
          await navigator.clipboard.writeText(value);
          flashFeedback(root, 'Texto copiado');
        } catch (error) {}
      });
    }

    if (clearBtn && textarea) {
      clearBtn.addEventListener('click', () => {
        textarea.value = '';
        flashFeedback(root, 'Texto limpiado');
      });
    }

    const shareBtn = qs(root, '[data-lada-share]');
    if (shareBtn) {
      shareBtn.addEventListener('click', async () => {
        const data = {
          title: document.title,
          text: root.dataset.shareText || '',
          url: window.location.href
        };

        if (navigator.share) {
          try {
            await navigator.share(data);
          } catch (error) {}
        } else {
          try {
            await navigator.clipboard.writeText(window.location.href);
            flashFeedback(root, 'Enlace copiado');
          } catch (error) {}
        }
      });
    }

    const printBtn = qs(root, '[data-lada-print]');
    if (printBtn) {
      printBtn.addEventListener('click', () => window.print());
    }

    if (finishSentinel && 'IntersectionObserver' in window) {
      const completeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || completedByScroll) return;

            completedByScroll = true;
            completedByTimer = true;

            const localSnap = markCompleted(ritualId, weeklyGoal, ritualTitle);
            const globalSnap = markCompletedGlobal(weeklyGoal);

            updateStatsUI(
              root,
              {
                totalDone: globalSnap.totalDone,
                streak: globalSnap.streak,
                todayDone: globalSnap.todayDone,
                weeklyDone: globalSnap.weeklyDone,
                saved: localSnap.saved,
                totalCompletions: localSnap.totalCompletions
              },
              weeklyGoal
            );
            renderRanking(root);

            if (interval === null) {
              hideSilenceCard();
              hideCompleteCard();

              fadeOutAudio(800, () => {
                window.setTimeout(() => {
                  playBell();

                  window.setTimeout(() => {
                    showSilenceCard();

                    window.setTimeout(() => {
                      scrollToCompletionArea(silenceCard, -10);
                    }, 120);

                    window.setTimeout(() => {
                      hideSilenceCard();
                      showCompleteCard();
                      celebrateCompletion(root);

                      window.setTimeout(() => {
                        scrollToCompletionArea(completeCard, -10);
                      }, 120);
                    }, 3400);
                  }, 450);
                }, 500);
              });
            } else {
              celebrateCompletion(root);
            }
          });
        },
        { threshold: 0.8 }
      );

      completeObserver.observe(finishSentinel);
    }

    function animateStatsOnView() {
      const stats = qsa(root, '[data-lada-total-done], [data-lada-streak], [data-lada-today]');
  
      stats.forEach((el, index) => {
        setTimeout(() => {
          el.classList.remove('lada-stat-bump');
          void el.offsetWidth;
          el.classList.add('lada-stat-bump');
        }, index * 120);
      });
    }

    if ('IntersectionObserver' in window) {
      const statsContainer = qs(root, '[data-lada-stats]') || root;

      const statsObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            animateStatsOnView();
            statsObserver.disconnect(); // solo una vez
          }
        });
      }, { threshold: 0.4 });

      statsObserver.observe(statsContainer);
    }

    setupSmoothScroll(root);
    syncStepTitles(root);
    setupStepObserver(root);
    setupEmotionChips(root);
    renderTime();
    renderPhase();
    setButtonLabels('idle');
    updateAudioUI(false);

    if (window.matchMedia) {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      const listener = () => applyNightMode(root);

      if (media.addEventListener) {
        media.addEventListener('change', listener);
      } else if (media.addListener) {
        media.addListener(listener);
      }
    }
  }

  function init(scope = document) {
    qsa(scope, '[data-lada-ritual-root]').forEach((root) => {
      try {
        initRitual(root);
      } catch (error) {
        console.error('LadaRitualEngine error:', error, root);
      }
    });
  }

  return { init };
})();

document.addEventListener('DOMContentLoaded', () => {
  
  const hour = new Date().getHours();

  const prefersDark =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  if (hour >= 20 || hour <= 6 || prefersDark) {
    document.querySelectorAll('[data-lada-ritual-root]').forEach((ritualRoot) => {
      ritualRoot.classList.add('lada-night');
    });
  }

  window.LadaRitualEngine.init(document);
  
});

document.addEventListener('shopify:section:load', (event) => {
  const container = event?.target || document;
  window.LadaRitualEngine.init(container);
});
